import React from "react";
import { Navigate } from "react-router-dom";
import Loading from "react-fullscreen-loading";
import { useAuth } from "./AuthContext";

// Where each portal goes when it is not allowed to be here.
const HOME_FOR = {
  admin:    "/admin/dashboard",
  reseller: "/admin/resellerdashboard",
  user:     "/user/dashboard",
};

// Guards the admin-area screens.
//
// Two independent checks:
//
//   1. PORTAL — which userType may be here. Defaults to admin only. Note this is
//      the portal, not the role: Sales, Marketing and every other admin-portal
//      role reports userType "admin" and passes, which is what lets new roles be
//      created without touching routing.
//
//      A few screens are genuinely shared with resellers — the Users and
//      Licenses lists render the same table for both, just against a row-scoped
//      endpoint. Those opt in with portals={["admin", "reseller"]}. Everything
//      else keeps resellers out, where before this guard let them into every
//      admin screen and each page patched over it with its own `isReseller`
//      checks.
//
//   2. PERMISSION — optional. Keeps a role out of a page whose data it could not
//      load anyway, so it lands somewhere useful instead of on a screen that
//      fills with 403 toasts:
//
//        <AdminRoute permission="admin.licenses.index">…</AdminRoute>
//
//      Pass an array to mean "any of these", which is how a shared screen states
//      the admin and reseller variants of the same capability:
//
//        permission={["admin.users.index", "reseller.users.index"]}
//
// This is presentation only. The server re-checks every request regardless, so a
// user who edits their way past this still gets a 403 from the API.
export const AdminRoute = ({ children, permission, portals = ["admin"] }) => {
  const { status, userType, can } = useAuth();

  if (status === "loading") {
    return <Loading loading={true} background="#fff" loaderColor="#883484" />;
  }
  if (status === "anon") {
    return <Navigate to="/signin" replace />;
  }

  if (!portals.includes(userType)) {
    return <Navigate to={HOME_FOR[userType] || "/signin"} replace />;
  }

  if (permission && !can(permission)) {
    return <Navigate to={HOME_FOR[userType] || "/signin"} replace />;
  }

  return children;
};
